/**
 * Order is a farthest-point traversal: recompute on membership change, never
 * append. Wash shade varies per family and per theme deliberately; one uniform
 * step leaves the closest pair indistinguishable.
 */
export const PATIENT_TAB_COLORS = [
  {
    family: 'blue',
    step: 9,
    edge: '#0090FF',
    edgeDark: '#0090FF',
    wash: '#8EC8F6',
    washDark: '#104D87',
  },
  {
    family: 'yellow',
    step: 9,
    edge: '#FFE629',
    edgeDark: '#FFE629',
    wash: '#E4C767',
    washDark: '#524202',
  },
  {
    family: 'pink',
    step: 9,
    edge: '#D6409F',
    edgeDark: '#D6409F',
    wash: '#FBDCEF',
    washDark: '#4B143D',
  },
  {
    family: 'bronze',
    step: 8,
    edge: '#C2A499',
    edgeDark: '#6F5F58',
    wash: '#D3BCB3',
    washDark: '#5A4C47',
  },
  {
    family: 'teal',
    step: 11,
    edge: '#008573',
    edgeDark: '#0BD8B6',
    wash: '#A1DED2',
    washDark: '#145750',
  },
  {
    family: 'tomato',
    step: 11,
    edge: '#D13415',
    edgeDark: '#FF977D',
    wash: '#FFCDC2',
    washDark: '#4E1511',
  },
  {
    family: 'iris',
    step: 11,
    edge: '#5753C6',
    edgeDark: '#B1A9FF',
    wash: '#B8BAF8',
    washDark: '#4A4A95',
  },
  {
    family: 'sky',
    step: 10,
    edge: '#74DAF8',
    edgeDark: '#A8EEFF',
    wash: '#D1F0FA',
    washDark: '#1F6692',
  },
  {
    family: 'indigo',
    step: 8,
    edge: '#8DA4EF',
    edgeDark: '#435DB1',
    wash: '#D2DEFF',
    washDark: '#1D2E62',
  },
  {
    family: 'brown',
    step: 9,
    edge: '#AD7F58',
    edgeDark: '#AD7F58',
    wash: '#F0E4D9',
    washDark: '#322922',
  },
  {
    family: 'orange',
    step: 9,
    edge: '#F76B15',
    edgeDark: '#F76B15',
    wash: '#FFDFB5',
    washDark: '#66350C',
  },
  {
    family: 'cyan',
    step: 8,
    edge: '#3DB9CF',
    edgeDark: '#11809C',
    wash: '#7DCEDC',
    washDark: '#003848',
  },
  {
    family: 'lime',
    step: 8,
    edge: '#8DB654',
    edgeDark: '#577538',
    wash: '#E2F0BD',
    washDark: '#29371D',
  },
  {
    family: 'jade',
    step: 9,
    edge: '#29A383',
    edgeDark: '#29A383',
    wash: '#D6F1E3',
    washDark: '#0B3B2C',
  },
  {
    family: 'amber',
    step: 9,
    edge: '#FFC53D',
    edgeDark: '#FFC53D',
    wash: '#FFEE9C',
    washDark: '#3F2700',
  },
];

// Patient where known, contact otherwise, conversation last. Namespaced, and
// identifier_type included, so no two identities collide. Null for no tab.
export const patientKeyForTab = tab => {
  if (!tab) return null;

  const sender = tab.meta?.sender;
  if (sender?.identifier) {
    return `patient:${sender.identifier_type || '-'}:${sender.identifier}`;
  }
  if (sender?.id) return `contact:${sender.id}`;

  return `conversation:${tab.id}`;
};

const isValidSlot = slot =>
  Number.isInteger(slot) && slot >= 0 && slot < PATIENT_TAB_COLORS.length;

/**
 * Returns a new map of patient key to slot; never mutates its inputs. A patient
 * takes the lowest slot no open patient holds. Entries are parked, not dropped,
 * when the last tab closes: a tab switch is REMOVE_TAB then ADD_TAB, so
 * dropping would recolour the strip mid-click.
 */
export const reconcilePatientTabColors = ({
  tabs,
  closedTabs,
  colors,
} = {}) => {
  const openTabs = Array.isArray(tabs) ? tabs : [];
  const parkedTabs = Array.isArray(closedTabs) ? closedTabs : [];

  // Corrupted localStorage can hand us anything.
  const stored =
    colors && typeof colors === 'object' && !Array.isArray(colors)
      ? colors
      : {};
  const next = {};
  Object.keys(stored).forEach(key => {
    if (isValidSlot(stored[key])) next[key] = stored[key];
  });

  // Contact sync can add an identifier mid-session, changing the key.
  [...openTabs, ...parkedTabs].forEach(tab => {
    const key = patientKeyForTab(tab);
    if (!key || next[key] !== undefined) return;

    const contactId = tab.meta?.sender?.id;
    if (!contactId) return;

    const staleKey = [`contact:${contactId}`, `conversation:${tab.id}`].find(
      candidate => candidate !== key && next[candidate] !== undefined
    );
    if (staleKey) {
      next[key] = next[staleKey];
      delete next[staleKey];
    }
  });

  const held = new Set();
  openTabs.forEach(tab => {
    const key = patientKeyForTab(tab);
    if (key && next[key] !== undefined) held.add(next[key]);
  });

  // Tab order keeps this a function of its inputs.
  openTabs.forEach(tab => {
    const key = patientKeyForTab(tab);
    if (!key || next[key] !== undefined) return;

    const slot = PATIENT_TAB_COLORS.findIndex((_, index) => !held.has(index));
    if (slot === -1) return;

    Object.keys(next).forEach(other => {
      if (next[other] === slot) delete next[other];
    });
    next[key] = slot;
    held.add(slot);
  });

  return next;
};
